'use client';

import { Check, Copy } from 'lucide-react';
import * as React from 'react';

interface SetupConnectCopyFieldProps {
  label: string;
  value: string;
  copyAriaLabel: string;
}

export function SetupConnectCopyField({ label, value, copyAriaLabel }: SetupConnectCopyFieldProps) {
  const [isCopied, setIsCopied] = React.useState(false);
  const resetTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Clipboard unavailable — user can still select the text manually
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <p className="text-muted-foreground m-0 text-xs font-medium leading-[1.3]">{label}</p>
      <div className="border-border bg-background flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5">
        <code className="text-foreground flex-1 overflow-x-auto whitespace-nowrap text-xs leading-[1.4]">
          {value}
        </code>
        <button
          type="button"
          aria-label={copyAriaLabel}
          className="text-muted-foreground hover:text-foreground inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 transition-colors duration-150"
          onClick={handleCopy}
        >
          {isCopied ? (
            <Check className="size-3.5 text-[color-mix(in_oklch,var(--primary)_85%,var(--foreground))]" aria-hidden />
          ) : (
            <Copy className="size-3.5" aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}
